import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

export interface LoginAuthErrorResult {
  errorMessages: string[];
  warningMessages: string[];
  interpolateParams: Record<string, string>;
  loginMessagesDataTest: string | undefined;
  shouldChangePassword: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LoginFormService {
  private readonly translateService = inject(TranslateService);

  public transformErrorMessages(form: FormGroup): string[] {
    let errorMessage: string[] = [];
    for (const field of Object.keys(form.controls)) {
      const errors = form.get(field)?.errors;
      if (!errors) {
        continue;
      }

      Object.keys(errors).forEach((errorKey: string) => {
        switch (errorKey) {
          case 'matchPassword':
          case 'matchPasswords':
            errorMessage = ['password.not.match'];
            break;
          case 'email':
            errorMessage = ['customerreg.message.validemail'];
            break;
          case 'minlength':
          case 'passwordSpecialCharacters':
            errorMessage = [
              'change-password.string2',
              'change-password.string3',
              'change-password.string4',
              'change-password.string5',
              'change-password.string6'
            ];
        }
      });
    }

    return errorMessage;
  }

  public mapLoginAuthError(error: {
    message?: string;
    status?: number;
    error?: { code?: string };
  }): LoginAuthErrorResult {
    if (error.message === 'changePassword') {
      return {
        errorMessages: [],
        warningMessages: ['login.error.explainChange'],
        interpolateParams: {},
        loginMessagesDataTest: 'warningChangePassword',
        shouldChangePassword: true
      };
    }

    if (error.error?.code === 'PASSWORD_EXPIRED') {
      return {
        errorMessages: [],
        warningMessages: ['login.error.passwordExpired'],
        interpolateParams: this.createForgotInterpolateParams(),
        loginMessagesDataTest: 'warningChangePassword',
        shouldChangePassword: false
      };
    }

    if (error.status === 504 || error.status === 503) {
      return {
        errorMessages: ['login.error.serviceUnavailable'],
        warningMessages: [],
        interpolateParams: {},
        loginMessagesDataTest: 'errorServiceUnavailable',
        shouldChangePassword: false
      };
    }

    return {
      errorMessages: ['login.error.passwordNotCorrect', 'login.error.passwordNotCorrectExpires'],
      warningMessages: [],
      interpolateParams: this.createForgotInterpolateParams(),
      loginMessagesDataTest: 'errorPasswordNotCorrect',
      shouldChangePassword: false
    };
  }

  private createForgotInterpolateParams(): Record<string, string> {
    return {
      forgot: this.translateService.instant('login.forgot')
    };
  }
}
